// TrustCenterEngagementPulse.jsx
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveLine } from '@nivo/line';

import UnifiedIcon from '../../../components/UnifiedIcon';
import IconHoverText from '../../../components/icon-hover-text/IconHoverText';
import KincodeButton from '../../../components/button/KincodeButton';

// ------------------------------------------------------------------------------------------------
// Supporting functions
// ------------------------------------------------------------------------------------------------
function KPI({ title, scorePercent, deltaPercent, colorHex, trend }) {
  const score = typeof scorePercent === 'number' ? scorePercent : null;
  const delta = typeof deltaPercent === 'number' ? deltaPercent : null;

  const lineData = useMemo(() => {
    if (!Array.isArray(trend) || trend.length === 0) return null;
    return [
      {
        id: 'trend',
        data: trend.map((p) => {
          const y =
            typeof p.rawScorePercent === 'number'
              ? p.rawScorePercent
              : typeof p.scorePercent === 'number'
                ? p.scorePercent
                : null;
          if (y == null) return null;
          return {
            x: `${p.year}-${String(p.month).padStart(2, '0')}`,
            y,
          };
        }).filter(Boolean),
      },
    ];
  }, [trend]);

  return (
    <div className="bg-white rounded-kcWidgets border border-gray-100 shadow-sm p-5">
      <div className="flex items-start justify-between">
        <div className="text-sm text-gray-500">{title}</div>
        <UnifiedIcon name="ChartLineUp" library="phosphor" className="h-5 w-5 text-gray-300" />
      </div>
      <div className="mt-3 flex items-end gap-3">
        <div className="text-3xl font-semibold text-gray-900">
          {score == null ? '—' : `${score}%`}
        </div>
        {delta != null && (
          <div className={`text-sm font-medium ${delta >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
            {delta >= 0 ? '+' : ''}
            {delta.toFixed(1)}%
          </div>
        )}
      </div>
      {lineData && (
        <div className="mt-4 h-20">
          <ResponsiveLine
            data={lineData}
            margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: 0, max: 100, stacked: false }}
            curve="monotoneX"
            axisBottom={null}
            axisLeft={null}
            enableGridX={false}
            enableGridY={false}
            colors={[colorHex || '#14b8a6']}
            lineWidth={3}
            enablePoints
            pointSize={4}
            pointColor={colorHex || '#14b8a6'}
            pointBorderWidth={1}
            pointBorderColor="#ffffff"
            enableArea
            areaBaselineValue={0}
            areaOpacity={0.35}
            useMesh
            enableCrosshair
            isInteractive
            tooltip={({ point }) => (
              <div
                className="nivo-tooltip text-[11px]"
                style={{ minWidth: 200 }}
              >
                <div className="font-medium">{point.data.yFormatted}%</div>
                <div className="opacity-80">{point.data.xFormatted}</div>
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
}

export default function TrustCenterEngagementPulse({ isLoading, kpis, colorHex }) {
  const { t } = useTranslation(['trust-center']);

  const [showAll, setShowAll] = useState(false);

  const items = Array.isArray(kpis) ? kpis : [];

  const visibleItems = !isLoading && !showAll ? items.slice(0, 6) : items;

  if (!isLoading && items.length === 0) {
    return null;
  }

  return (
    <>
      <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            {t('trust-center.engagement-pulse.title')}
            <IconHoverText
              text={t('trust-center.engagement-pulse.subtitle')}
              icon={
                <UnifiedIcon
                  name="Info"
                  library="phosphor"
                  className="w-4 h-4 text-gray-400"
                />
              }
              width={4}
              height={4}
            />
          </h2>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {isLoading
          ? Array.from({ length: 6 }).map((_, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div
                key={idx}
                className="bg-white rounded-kcWidgets border border-gray-100 shadow-sm p-5 animate-pulse space-y-4"
              >
                <div className="h-3 w-32 rounded-full bg-gray-100" />
                <div className="h-6 w-20 rounded-full bg-gray-100" />
                <div className="h-2 w-full rounded-full bg-gray-100" />
              </div>
            ))
          : visibleItems.map((kpi) => (
              <KPI
                key={kpi.key}
                title={kpi.label}
                scorePercent={kpi.rawScorePercent}
                deltaPercent={kpi.deltaPercent}
                colorHex={colorHex}
                trend={kpi.trend || []}
              />
            ))}
      </div>

      {!isLoading && items.length > 6 && (
        <div className="mt-4 flex justify-center">
          <KincodeButton
            variant="tertiary"
            theme="light"
            onClick={() => setShowAll((prev) => !prev)}
            className="text-xs"
            ariaLabel={
              showAll
                ? t('trust-center.engagement-pulse.show-top-6')
                : t('trust-center.engagement-pulse.show-all')
            }
          >
            <span className="inline-flex items-center gap-1">
              <span>
                {showAll
                  ? t('trust-center.engagement-pulse.show-top-6')
                  : t('trust-center.engagement-pulse.show-all')}
              </span>
              <UnifiedIcon
                name={showAll ? 'CaretUp' : 'CaretDown'}
                library="phosphor"
                className="w-3 h-3"
              />
            </span>
          </KincodeButton>
        </div>
      )}
    </>
  );
}

